import { Badge, Box, Group, Paper, SimpleGrid, Stack, Text, Tooltip } from "@mantine/core";
import { CompositeChart } from "@mantine/charts";
import { useTranslation } from "react-i18next";
import {
  ObjectivePhoriaConditionSummary,
  ObjectivePhoriaOcclusion,
  ObjectivePhoriaResult,
} from "types/testTypes";
import {
  HorizontalDirectionToken,
  HorizontalMagnitudeToken,
  PhoriaQualityToken,
  PhoriaShiftMagnitude,
  VerticalDirectionToken,
  VerticalMagnitudeToken,
  classifyPhoriaShift,
  getPhoriaColor,
  getPhoriaQualityColor,
  horizontalDirectionToken,
  horizontalMagnitudeToken,
  isAxisReliable,
  isHorizontalReliable,
  isVerticalReliable,
  phoriaQualityToken,
  verticalDirectionToken,
  verticalMagnitudeToken,
} from "../../../overview_pages/utils/phoriaCalculations";

type ObjectivePhoriaTestEntryProps = {
  objectivePhoria: ObjectivePhoriaResult;
};

type TrendPoint = {
  sequence: string;
  horizontalPd?: number;
  verticalPd?: number;
};

type ConditionPoint = {
  condition: string;
  horizontalPd: number;
  verticalPd: number;
};

const QUALITY_LABEL_KEY: Record<PhoriaQualityToken, string> = {
  good: "coverObjectivePhoriaQualityGood",
  near_orthophoria: "coverObjectivePhoriaQualityNearOrthophoria",
  no_strong_tendency: "coverObjectivePhoriaQualityNoStrongTendency",
  caution: "coverObjectivePhoriaQualityCaution",
  unknown: "coverObjectivePhoriaQualityUnknown",
};

const QUALITY_DESC_KEY: Record<PhoriaQualityToken, string> = {
  good: "coverObjectivePhoriaQualityDescGood",
  near_orthophoria: "coverObjectivePhoriaQualityDescNearOrthophoria",
  no_strong_tendency: "coverObjectivePhoriaQualityDescNoStrongTendency",
  caution: "coverObjectivePhoriaQualityDescCaution",
  unknown: "coverObjectivePhoriaQualityDescUnknown",
};

const SHIFT_LINE_KEY: Record<PhoriaShiftMagnitude, string> = {
  minimal: "coverObjectivePhoriaClinicalLineShiftMinimal",
  typical: "coverObjectivePhoriaClinicalLineShiftTypical",
  notable: "coverObjectivePhoriaClinicalLineShiftNotable",
  marked: "coverObjectivePhoriaClinicalLineShiftMarked",
};

const H_MAGNITUDE_KEY: Record<HorizontalMagnitudeToken, string> = {
  not_measurable: "coverObjectivePhoriaMagnitudeNotMeasurable",
  near_orthophoria: "coverObjectivePhoriaMagnitudeNearOrthophoria",
  mild: "coverObjectivePhoriaMagnitudeMildHorizontal",
  moderate: "coverObjectivePhoriaMagnitudeModerateHorizontal",
  large: "coverObjectivePhoriaMagnitudeLargeHorizontal",
};

const V_MAGNITUDE_KEY: Record<VerticalMagnitudeToken, string> = {
  not_measurable: "coverObjectivePhoriaMagnitudeNotMeasurable",
  minimal: "coverObjectivePhoriaMagnitudeMinimalVertical",
  small: "coverObjectivePhoriaMagnitudeSmallVertical",
  notable: "coverObjectivePhoriaMagnitudeNotableVertical",
};

const H_DIRECTION_KEY: Record<HorizontalDirectionToken, string> = {
  not_available: "coverObjectivePhoriaDirectionNotAvailable",
  near_orthophoria: "coverObjectivePhoriaDirectionNearOrthophoria",
  eso: "coverObjectivePhoriaDirectionEso",
  exo: "coverObjectivePhoriaDirectionExo",
};

const V_DIRECTION_KEY: Record<VerticalDirectionToken, string> = {
  not_available: "coverObjectivePhoriaDirectionNotAvailable",
  minimal: "coverObjectivePhoriaDirectionMinimalVertical",
  left_hyper: "coverObjectivePhoriaDirectionLeftHyper",
  right_hyper: "coverObjectivePhoriaDirectionRightHyper",
};

const formatPd = (value?: number | null) =>
  Number.isFinite(value) ? `${Number(value).toFixed(2)} PD` : "-";

const formatDeg = (value?: number | null) =>
  Number.isFinite(value) ? `${Number(value).toFixed(2)} deg` : "-";

const labelForCondition = (conditionName: string, t: (key: string) => string) =>
  conditionName === "near"
    ? t("coverObjectivePhoriaConditionNear")
    : t("coverObjectivePhoriaConditionDistance");

const labelForPhase = (action: string, t: (key: string) => string) => {
  if (action.includes("CoverRight")) {
    return t("coverObjectivePhoriaPhaseCoverRight");
  }

  if (action.includes("CoverLeft")) {
    return t("coverObjectivePhoriaPhaseCoverLeft");
  }

  if (action.includes("Open")) {
    return t("coverObjectivePhoriaPhaseBinocular");
  }

  return action;
};

const describeHorizontalDirection = (value: number | null | undefined, t: (k: string) => string) =>
  t(H_DIRECTION_KEY[horizontalDirectionToken(value)]);

const describeVerticalDirection = (value: number | null | undefined, t: (k: string) => string) =>
  t(V_DIRECTION_KEY[verticalDirectionToken(value)]);

const classifyHorizontalMagnitude = (value: number | null | undefined, t: (k: string) => string) =>
  t(H_MAGNITUDE_KEY[horizontalMagnitudeToken(value)]);

const classifyVerticalMagnitude = (value: number | null | undefined, t: (k: string) => string) =>
  t(V_MAGNITUDE_KEY[verticalMagnitudeToken(value)]);

const getQualityLabel = (qualityFlag: string | undefined, t: (key: string) => string) =>
  t(QUALITY_LABEL_KEY[phoriaQualityToken(qualityFlag)]);

const getQualityDescription = (qualityFlag: string | undefined, t: (key: string) => string) =>
  t(QUALITY_DESC_KEY[phoriaQualityToken(qualityFlag)]);

const buildConditionSeries = (
  conditionSummaries: ObjectivePhoriaConditionSummary[],
  t: (key: string) => string,
): ConditionPoint[] =>
  conditionSummaries.map((summary) => ({
    condition: labelForCondition(summary.conditionName, t),
    horizontalPd: summary.horizontalPhoriaPd ?? 0,
    verticalPd: summary.verticalPhoriaPd ?? 0,
  }));

const buildTrendSeries = (
  occlusions: ObjectivePhoriaOcclusion[],
  t: (key: string) => string,
): TrendPoint[] =>
  occlusions.map((occlusion, index) => ({
    sequence: `${labelForCondition(occlusion.conditionName, t).slice(0, 1)}${occlusion.cycleIndex}-${index + 1}`,
    horizontalPd: occlusion.horizontalDeltaPd,
    verticalPd: occlusion.verticalDeltaPd,
  }));

const buildClinicalImpression = ({
  distance,
  near,
  occlusions,
  overallQualityFlag,
  t,
}: {
  distance?: ObjectivePhoriaConditionSummary;
  near?: ObjectivePhoriaConditionSummary;
  occlusions?: ObjectivePhoriaResult["occlusions"];
  overallQualityFlag?: string;
  t: (key: string, options?: Record<string, unknown>) => string;
}) => {
  const lines: string[] = [];

  // Per-axis lines respect the IQR reliability gate — if cycle-to-cycle
  // readings disagreed too much, we don't claim a magnitude/direction.
  if (isHorizontalReliable(distance, occlusions)) {
    lines.push(
      t("coverObjectivePhoriaClinicalLineDistanceHorizontal", {
        magnitude: classifyHorizontalMagnitude(distance?.horizontalPhoriaPd, t),
        value: formatPd(distance?.horizontalPhoriaPd),
        direction: describeHorizontalDirection(distance?.horizontalPhoriaPd, t),
      }),
    );
  }
  if (isHorizontalReliable(near, occlusions)) {
    lines.push(
      t("coverObjectivePhoriaClinicalLineNearHorizontal", {
        magnitude: classifyHorizontalMagnitude(near?.horizontalPhoriaPd, t),
        value: formatPd(near?.horizontalPhoriaPd),
        direction: describeHorizontalDirection(near?.horizontalPhoriaPd, t),
      }),
    );
  }
  if (isVerticalReliable(distance, occlusions)) {
    lines.push(
      t("coverObjectivePhoriaClinicalLineDistanceVertical", {
        magnitude: classifyVerticalMagnitude(distance?.verticalPhoriaPd, t),
        value: formatPd(distance?.verticalPhoriaPd),
      }),
    );
  }
  if (isVerticalReliable(near, occlusions)) {
    lines.push(
      t("coverObjectivePhoriaClinicalLineNearVertical", {
        magnitude: classifyVerticalMagnitude(near?.verticalPhoriaPd, t),
        value: formatPd(near?.verticalPhoriaPd),
      }),
    );
  }

  if (
    isHorizontalReliable(distance, occlusions) &&
    isHorizontalReliable(near, occlusions) &&
    Number.isFinite(distance?.horizontalPhoriaPd) &&
    Number.isFinite(near?.horizontalPhoriaPd)
  ) {
    const shift = Number(near?.horizontalPhoriaPd) - Number(distance?.horizontalPhoriaPd);
    lines.push(
      t(SHIFT_LINE_KEY[classifyPhoriaShift(shift)], {
        value: formatPd(shift),
      }),
    );
  }

  // Variability indicator uses IQR — robust to outliers, aligned with the
  // lazy-eye summary card's reliability gate.
  if (Number.isFinite(distance?.horizontalIqrPd) && Number(distance?.horizontalIqrPd) > 2) {
    lines.push(
      t("coverObjectivePhoriaClinicalLineDistanceVariability", {
        value: formatPd(distance?.horizontalIqrPd),
      }),
    );
  }

  if (Number.isFinite(near?.horizontalIqrPd) && Number(near?.horizontalIqrPd) > 2) {
    lines.push(
      t("coverObjectivePhoriaClinicalLineNearVariability", {
        value: formatPd(near?.horizontalIqrPd),
      }),
    );
  }

  if (phoriaQualityToken(overallQualityFlag) === "caution") {
    lines.push(
      t("coverObjectivePhoriaClinicalLineQualityCaution", {
        qualityFlag: getQualityLabel(overallQualityFlag, t),
      }),
    );
  }

  return lines;
};

const ConditionCard = ({
  badgeColor,
  badgeLabel,
  equivalentLabel,
  summary,
  occlusions,
  t,
}: {
  badgeColor: string;
  badgeLabel: string;
  equivalentLabel: string;
  summary?: ObjectivePhoriaConditionSummary;
  occlusions?: ObjectivePhoriaResult["occlusions"];
  t: (key: string) => string;
}) => {
  const hReliable = isHorizontalReliable(summary, occlusions);
  const vReliable = isVerticalReliable(summary, occlusions);
  const unreliableLabel = `(${t("coverObjectivePhoriaUnreliableAxis")})`;

  return (
    <Paper withBorder radius="md" p="md">
      <Stack gap={6}>
        <Group gap={8} justify="space-between" wrap="nowrap">
          <Group gap={8} wrap="nowrap">
            <Badge variant="filled" color={badgeColor}>
              {badgeLabel}
            </Badge>
            <Text fw={600}>{equivalentLabel}</Text>
          </Group>
          <Tooltip
            label={getQualityDescription(summary?.qualityFlag, t)}
            multiline
            w={240}
            withArrow
          >
            <Badge color={getPhoriaQualityColor(summary?.qualityFlag)} variant="filled">
              {getQualityLabel(summary?.qualityFlag, t)}
            </Badge>
          </Tooltip>
        </Group>
        <Text>
          {t("coverObjectivePhoriaHorizontalPhoriaLabel")}:{" "}
          <Text
            span
            fw={700}
            c={hReliable ? getPhoriaColor(summary?.horizontalPhoriaPd) : "dimmed"}
          >
            {formatPd(summary?.horizontalPhoriaPd)}
          </Text>{" "}
          {hReliable ? (
            <>({describeHorizontalDirection(summary?.horizontalPhoriaPd, t)})</>
          ) : (
            <Text span size="sm" c="red.7" fs="italic">
              {unreliableLabel}
            </Text>
          )}
        </Text>
        <Text>
          {t("coverObjectivePhoriaVerticalPhoriaLabel")}:{" "}
          <Text span fw={700} c={vReliable ? undefined : "dimmed"}>
            {formatPd(summary?.verticalPhoriaPd)}
          </Text>{" "}
          {vReliable ? (
            <>({describeVerticalDirection(summary?.verticalPhoriaPd, t)})</>
          ) : (
            <Text span size="sm" c="red.7" fs="italic">
              {unreliableLabel}
            </Text>
          )}
        </Text>
        <Text size="sm" c="dimmed">
          {t("coverObjectivePhoriaSdLabel")}: H {formatPd(summary?.horizontalSdPd)} | V{" "}
          {formatPd(summary?.verticalSdPd)} · {t("coverObjectivePhoriaIqrLabel")}: H{" "}
          <Text span c={isAxisReliable(summary?.horizontalIqrPd) ? "dimmed" : "red.7"}>
            {formatPd(summary?.horizontalIqrPd)}
          </Text>{" "}
          | V{" "}
          <Text span c={isAxisReliable(summary?.verticalIqrPd) ? "dimmed" : "red.7"}>
            {formatPd(summary?.verticalIqrPd)}
          </Text>{" "}
          · {t("coverObjectivePhoriaValidOcclusionsCountLabel")}:{" "}
          {summary?.validOcclusionCount ?? 0}
        </Text>
      </Stack>
    </Paper>
  );
};

export default function ObjectivePhoriaTestEntry({
  objectivePhoria,
}: ObjectivePhoriaTestEntryProps) {
  const { t } = useTranslation();

  const distanceSummary = objectivePhoria.conditionSummaries?.find(
    (summary) => summary.conditionName === "distance",
  );
  const nearSummary = objectivePhoria.conditionSummaries?.find(
    (summary) => summary.conditionName === "near",
  );

  const conditionSeries = buildConditionSeries(objectivePhoria.conditionSummaries ?? [], t);
  const trendSeries = buildTrendSeries(objectivePhoria.occlusions ?? [], t);
  const clinicalImpression = buildClinicalImpression({
    distance: distanceSummary,
    near: nearSummary,
    occlusions: objectivePhoria.occlusions,
    overallQualityFlag: objectivePhoria.overallQualityFlag,
    t,
  });

  return (
    <Stack gap="md" justify="center" align="center" mb="sm">
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm" w="100%" className="print-cols-2">
        <Paper withBorder radius="md" p="md" w="100%">
          <Stack gap={6}>
            <Group gap={8} justify="space-between" wrap="nowrap">
              <Text fw={700}>{t("coverObjectivePhoriaTitle")}</Text>
              <Tooltip
                label={getQualityDescription(objectivePhoria.overallQualityFlag, t)}
                multiline
                w={240}
                withArrow
              >
                <Badge
                  color={getPhoriaQualityColor(objectivePhoria.overallQualityFlag)}
                  variant="filled"
                >
                  {getQualityLabel(objectivePhoria.overallQualityFlag, t)}
                </Badge>
              </Tooltip>
            </Group>
            <Text c="dimmed">
              {t("coverObjectivePhoriaProtocolLabel")}: {objectivePhoria.protocol}
            </Text>
            <Text c="dimmed">
              {t("coverObjectivePhoriaPhaseDurationLabel")}: {objectivePhoria.phaseDurationSeconds}{" "}
              s
            </Text>
            <Text c="dimmed">
              {t("coverObjectivePhoriaCyclesPerConditionLabel")}:{" "}
              {objectivePhoria.cyclesPerCondition}
            </Text>
            <Text c="dimmed">
              {t("coverObjectivePhoriaEndpointWindowLabel")}:{" "}
              {objectivePhoria.endpointWindowSeconds} s
            </Text>
          </Stack>
        </Paper>

        <Paper withBorder radius="md" p="md" w="100%">
          <Stack gap={4}>
            <Text fw={600}>{t("coverObjectivePhoriaClinicalImpressionTitle")}</Text>
            {clinicalImpression.map((line) => (
              <Text key={line} size="sm">
                {line}
              </Text>
            ))}
          </Stack>
        </Paper>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm" w="100%" className="print-cols-2">
        <ConditionCard
          badgeColor="blue"
          badgeLabel={t("coverObjectivePhoriaConditionDistance")}
          equivalentLabel={t("coverObjectivePhoriaDistanceEquivalent")}
          summary={distanceSummary}
          occlusions={objectivePhoria.occlusions}
          t={t}
        />
        <ConditionCard
          badgeColor="teal"
          badgeLabel={t("coverObjectivePhoriaConditionNear")}
          equivalentLabel={t("coverObjectivePhoriaNearEquivalent")}
          summary={nearSummary}
          occlusions={objectivePhoria.occlusions}
          t={t}
        />
      </SimpleGrid>

      <Paper withBorder radius="md" p="md" w="100%">
        <Text fw={600} mb={6}>
          {t("coverObjectivePhoriaConditionMetricChartTitle")}
        </Text>
        <CompositeChart
          h={140}
          data={conditionSeries}
          dataKey="condition"
          series={[
            {
              name: "horizontalPd",
              type: "bar",
              color: "blue",
              label: t("coverObjectivePhoriaHorizontalSeriesLabel"),
            },
            {
              name: "verticalPd",
              type: "bar",
              color: "teal",
              label: t("coverObjectivePhoriaVerticalSeriesLabel"),
            },
          ]}
          withLegend
          xAxisProps={{
            tickMargin: 8,
            height: 30,
          }}
          yAxisProps={{
            tickMargin: 8,
            width: 50,
          }}
        />
      </Paper>

      <Paper withBorder radius="md" p="md" w="100%">
        <Text fw={600} mb={6}>
          {t("coverObjectivePhoriaOcclusionTrendChartTitle")}
        </Text>
        <CompositeChart
          h={140}
          data={trendSeries}
          dataKey="sequence"
          withLegend
          series={[
            {
              name: "horizontalPd",
              type: "line",
              color: "blue",
              label: t("coverObjectivePhoriaHorizontalSeriesLabel"),
            },
            {
              name: "verticalPd",
              type: "line",
              color: "teal",
              label: t("coverObjectivePhoriaVerticalSeriesLabel"),
            },
          ]}
          xAxisProps={{
            tickMargin: 8,
            height: 30,
          }}
          yAxisProps={{
            tickMargin: 8,
            width: 50,
          }}
        />
      </Paper>

      <Paper withBorder radius="md" p="md" w="100%">
        <Text fw={600} mb={6}>
          {t("coverObjectivePhoriaPerOcclusionTableTitle")}
        </Text>
        <Box component="table" w="100%" style={{ borderCollapse: "collapse", fontSize: "0.88rem" }}>
          <Box component="thead">
            <Box component="tr">
              {[
                t("coverObjectivePhoriaTableCondition"),
                t("coverObjectivePhoriaTableCycle"),
                t("coverObjectivePhoriaTablePhase"),
                t("coverObjectivePhoriaTableHorizontalDeg"),
                t("coverObjectivePhoriaTableHorizontalPd"),
                t("coverObjectivePhoriaTableVerticalDeg"),
                t("coverObjectivePhoriaTableVerticalPd"),
              ].map((header) => (
                <Box component="th" key={header} style={{ textAlign: "left", padding: "8px" }}>
                  {header}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {(objectivePhoria.occlusions ?? []).map((occlusion, index) => (
              <Box
                component="tr"
                key={`${occlusion.conditionName}-${occlusion.cycleIndex}-${index}`}
              >
                <Box component="td" style={{ padding: "8px" }}>
                  <Badge
                    variant="filled"
                    color={occlusion.conditionName === "near" ? "teal" : "blue"}
                  >
                    {labelForCondition(occlusion.conditionName, t)}
                  </Badge>
                </Box>
                <Box component="td" style={{ padding: "8px" }}>
                  {occlusion.cycleIndex}
                </Box>
                <Box component="td" style={{ padding: "8px" }}>
                  <Group gap={6}>
                    <Badge size="sm" variant="filled" color="dark">
                      {labelForPhase(occlusion.baselineAction, t)}
                    </Badge>
                    <Text size="xs" c="dimmed">
                      {t("coverObjectivePhoriaPhaseTransition")}
                    </Text>
                    <Badge size="sm" variant="filled" color="blue">
                      {labelForPhase(occlusion.occlusionAction, t)}
                    </Badge>
                  </Group>
                </Box>
                <Box component="td" style={{ padding: "8px" }}>
                  {formatDeg(occlusion.horizontalDeltaDeg)}
                </Box>
                <Box component="td" style={{ padding: "8px" }}>
                  {formatPd(occlusion.horizontalDeltaPd)}
                </Box>
                <Box component="td" style={{ padding: "8px" }}>
                  {formatDeg(occlusion.verticalDeltaDeg)}
                </Box>
                <Box component="td" style={{ padding: "8px" }}>
                  {formatPd(occlusion.verticalDeltaPd)}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>
    </Stack>
  );
}
